import React, { useState, useEffect, useCallback, Component } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ConfirmationNumber as TicketIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  FiberManualRecord as DotIcon,
  Dashboard as DashboardIcon,
  ViewList as ViewListIcon,
  ArrowBack as BackIcon,
  PriorityHigh as UrgentIcon,
  KeyboardArrowUp as HighIcon,
  Remove as MediumIcon,
  KeyboardArrowDown as LowIcon,
  Reply as ReplyIcon,
  Lock as InternalIcon,
  Public as ExternalIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import api, { extractData, handleApiError } from '../../utils/api';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

// Error Boundary to prevent blank page on crashes
class SupportErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error: string }
> {
  state = { hasError: false, error: '' };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error?.message || 'Unknown error' };
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Something went wrong loading Support
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {this.state.error}
          </Typography>
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            onClick={() => this.setState({ hasError: false, error: '' })}
          >
            Try Again
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Types
interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  userId: string | null;
  organizationId: number;
  assignedTo: string | null;
  requesterEmail: string | null;
  requesterName: string | null;
  isExternal: boolean;
  resolvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  userEmail?: string;
  userName?: string;
  assignedEmail?: string;
  assignedName?: string;
  replyCount?: number;
  replies?: TicketReply[];
  attachments?: any[];
  metadata?: any;
}

interface TicketReply {
  id: string;
  ticketId: string;
  userId: string | null;
  message: string;
  isStaffReply: boolean;
  isInternalNote: boolean;
  senderEmail: string | null;
  senderName: string | null;
  createdAt: string;
  userEmail?: string;
  userName?: string;
}

interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  urgent: number;
  high: number;
  external: number;
  last24h: number;
  last7d: number;
}

interface TicketsResponse {
  tickets: Ticket[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Constants
const STATUS_COLORS: Record<string, string> = {
  open: '#2196f3',
  in_progress: '#ff9800',
  resolved: '#4caf50',
  closed: '#9e9e9e',
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#f44336',
  high: '#ff5722',
  medium: '#ff9800',
  low: '#4caf50',
};

const getPriorityIcon = (priority: string) => {
  switch (priority) {
    case 'urgent': return <UrgentIcon sx={{ fontSize: 16 }} />;
    case 'high': return <HighIcon sx={{ fontSize: 16 }} />;
    case 'medium': return <MediumIcon sx={{ fontSize: 16 }} />;
    case 'low': return <LowIcon sx={{ fontSize: 16 }} />;
    default: return null;
  }
};

const CATEGORIES = ['general', 'billing', 'technical', 'account'];

// Helper components
const StatusChip: React.FC<{ status: string; size?: 'small' | 'medium' }> = ({ status, size = 'small' }) => (
  <Chip
    size={size}
    label={STATUS_LABELS[status] || status}
    sx={{
      bgcolor: `${STATUS_COLORS[status] || '#9e9e9e'}20`,
      color: STATUS_COLORS[status] || '#9e9e9e',
      fontWeight: 600,
      fontSize: size === 'small' ? '0.7rem' : '0.8rem',
      height: size === 'small' ? 22 : 28,
    }}
  />
);

const PriorityChip: React.FC<{ priority: string; size?: 'small' | 'medium' }> = ({ priority, size = 'small' }) => {
  const icon = getPriorityIcon(priority);
  return (
    <Chip
      size={size}
      icon={icon || undefined}
      label={priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'Medium'}
      sx={{
        bgcolor: `${PRIORITY_COLORS[priority] || '#9e9e9e'}15`,
        color: PRIORITY_COLORS[priority] || '#9e9e9e',
        fontWeight: 500,
        fontSize: size === 'small' ? '0.7rem' : '0.8rem',
        height: size === 'small' ? 22 : 28,
        '& .MuiChip-icon': { color: PRIORITY_COLORS[priority] },
      }}
    />
  );
};

const SupportPage: React.FC = () => {
  const { ticketId: urlTicketId } = useParams<{ ticketId?: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // API base path: admins use /support/tickets, users use /support/my-tickets
  const ticketsPath = isAdmin ? '/support/tickets' : '/support/my-tickets';

  // State
  const [view, setView] = useState<'list' | 'dashboard'>('list');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [newPriority, setNewPriority] = useState('medium');
  const [creating, setCreating] = useState(false);

  // Reply
  const [replyText, setReplyText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [replying, setReplying] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: 25 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;

      const res = await api.get(ticketsPath, { params });
      const data = extractData<TicketsResponse>(res);
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err: any) {
      const msg = handleApiError(err);
      setSnackbar({ open: true, message: msg || 'Failed to load tickets', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, priorityFilter, categoryFilter, ticketsPath]);

  // Fetch stats (admin only)
  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/support/stats');
      setStats(extractData<TicketStats>(res));
    } catch {
      // Stats are optional, don't block UI
    }
  }, [isAdmin]);

  // Fetch ticket detail
  const fetchTicketDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`${ticketsPath}/${id}`);
      setSelectedTicket(extractData<Ticket>(res));
    } catch (err: any) {
      const msg = handleApiError(err);
      setSnackbar({ open: true, message: msg || 'Failed to load ticket', severity: 'error' });
    } finally {
      setDetailLoading(false);
    }
  }, [ticketsPath]);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [fetchTickets, fetchStats]);

  // Load ticket from URL param
  useEffect(() => {
    if (urlTicketId) {
      fetchTicketDetail(urlTicketId);
    }
  }, [urlTicketId, fetchTicketDetail]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Create ticket
  const handleCreate = async () => {
    if (!newSubject.trim() || !newDescription.trim()) return;
    setCreating(true);
    try {
      await api.post('/support/tickets', {
        subject: newSubject.trim(),
        description: newDescription.trim(),
        category: newCategory,
        priority: newPriority,
      });
      setCreateOpen(false);
      setNewSubject('');
      setNewDescription('');
      setNewCategory('general');
      setNewPriority('medium');
      setSnackbar({ open: true, message: 'Ticket created successfully', severity: 'success' });
      fetchTickets();
      fetchStats();
    } catch (err: any) {
      const msg = handleApiError(err);
      setSnackbar({ open: true, message: msg || 'Failed to create ticket', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  // Update ticket
  const handleUpdateTicket = async (id: string, updates: Record<string, string>) => {
    try {
      await api.put(`${ticketsPath}/${id}`, updates);
      setSnackbar({ open: true, message: 'Ticket updated', severity: 'success' });
      fetchTicketDetail(id);
      fetchTickets();
      fetchStats();
    } catch (err: any) {
      const msg = handleApiError(err);
      setSnackbar({ open: true, message: msg || 'Failed to update ticket', severity: 'error' });
    }
  };

  // Add reply
  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setReplying(true);
    try {
      await api.post(`${ticketsPath}/${selectedTicket.id}/replies`, {
        message: replyText.trim(),
        ...(isAdmin ? { isInternalNote } : {}),
      });
      setReplyText('');
      setIsInternalNote(false);
      setSnackbar({ open: true, message: isInternalNote ? 'Internal note added' : 'Reply sent', severity: 'success' });
      fetchTicketDetail(selectedTicket.id);
      fetchTickets();
    } catch (err: any) {
      const msg = handleApiError(err);
      setSnackbar({ open: true, message: msg || 'Failed to send reply', severity: 'error' });
    } finally {
      setReplying(false);
    }
  };

  // Select ticket
  const handleSelectTicket = (ticket: Ticket) => {
    navigate(`/support/${ticket.id}`);
    fetchTicketDetail(ticket.id);
  };

  const handleBack = () => {
    setSelectedTicket(null);
    navigate('/support');
  };

  // =====================
  // Render helpers
  // =====================

  const renderStatsCards = () => {
    if (!stats) return null;
    const cards = [
      { label: 'Open', value: stats.open, color: STATUS_COLORS.open },
      { label: 'In Progress', value: stats.inProgress, color: STATUS_COLORS.in_progress },
      { label: 'Resolved', value: stats.resolved, color: STATUS_COLORS.resolved },
      { label: 'Total', value: stats.total, color: '#7c3aed' },
      { label: 'Last 24h', value: stats.last24h, color: '#0ea5e9' },
      { label: 'Urgent', value: stats.urgent, color: PRIORITY_COLORS.urgent },
    ];

    return (
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        {cards.map((c) => (
          <Paper
            key={c.label}
            elevation={0}
            sx={{
              flex: '1 1 140px',
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              minWidth: 120,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, color: c.color }}>
              {c.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {c.label}
            </Typography>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderTicketList = () => (
    <Box
      sx={{
        width: selectedTicket ? 360 : '100%',
        minWidth: selectedTicket ? 360 : undefined,
        maxWidth: selectedTicket ? 360 : undefined,
        borderRight: selectedTicket ? '1px solid' : 'none',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Search & Filters */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search tickets..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }} />,
            },
          }}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select
              displayEmpty
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              sx={{ fontSize: '0.75rem', height: 30 }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select
              displayEmpty
              value={priorityFilter}
              onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}
              sx={{ fontSize: '0.75rem', height: 30 }}
            >
              <MenuItem value="">All Priority</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select
              displayEmpty
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
              sx={{ fontSize: '0.75rem', height: 30 }}
            >
              <MenuItem value="">All Category</MenuItem>
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Ticket list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : tickets.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <TicketIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No tickets found</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {tickets.map((ticket) => (
              <ListItem
                key={ticket.id}
                disablePadding
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <ListItemButton
                  selected={selectedTicket?.id === ticket.id}
                  onClick={() => handleSelectTicket(ticket)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <DotIcon sx={{ fontSize: 10, color: STATUS_COLORS[ticket.status] || '#9e9e9e' }} />
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.7rem' }}
                      >
                        {ticket.ticketNumber}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {formatDistanceToNow(parseISO(ticket.createdAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ticket.subject}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StatusChip status={ticket.status} />
                      <PriorityChip priority={ticket.priority} />
                      {ticket.isExternal && (
                        <Chip size="small" icon={<ExternalIcon sx={{ fontSize: 12 }} />} label="External" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                      <Box sx={{ flex: 1 }} />
                      {(ticket.replyCount ?? 0) > 0 && (
                        <Chip
                          size="small"
                          icon={<ReplyIcon sx={{ fontSize: 12 }} />}
                          label={ticket.replyCount}
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}
                    >
                      {ticket.userName || ticket.requesterEmail || 'Unknown'}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center', gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </Button>
          <Typography variant="caption" sx={{ alignSelf: 'center' }}>
            {page} / {totalPages}
          </Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderTicketDetail = () => {
    if (!selectedTicket) return null;

    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <IconButton size="small" onClick={handleBack} sx={{ mt: 0.5 }}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
              >
                {selectedTicket.ticketNumber}
              </Typography>
              {selectedTicket.isExternal && (
                <Chip size="small" icon={<ExternalIcon sx={{ fontSize: 12 }} />} label="External" sx={{ height: 20, fontSize: '0.65rem' }} />
              )}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {selectedTicket.subject}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <StatusChip status={selectedTicket.status} size="medium" />
              <PriorityChip priority={selectedTicket.priority} size="medium" />
              <Chip
                size="small"
                label={selectedTicket.category?.charAt(0).toUpperCase() + selectedTicket.category?.slice(1)}
                variant="outlined"
                sx={{ height: 24 }}
              />
            </Box>
          </Box>
        </Box>

        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            {/* Ticket info bar */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                bgcolor: 'action.hover',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">Requester</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {selectedTicket.userName || selectedTicket.requesterName || selectedTicket.requesterEmail || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {selectedTicket.assignedName || 'Unassigned'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {format(parseISO(selectedTicket.createdAt), 'MMM d, yyyy h:mm a')}
                </Typography>
              </Box>
              {selectedTicket.resolvedAt && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Resolved</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {format(parseISO(selectedTicket.resolvedAt), 'MMM d, yyyy h:mm a')}
                  </Typography>
                </Box>
              )}

              {/* Quick actions (admin only) */}
              {isAdmin && (
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={selectedTicket.status}
                    onChange={(e) => handleUpdateTicket(selectedTicket.id, { status: e.target.value })}
                    sx={{ height: 32, fontSize: '0.8rem' }}
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={selectedTicket.priority}
                    onChange={(e) => handleUpdateTicket(selectedTicket.id, { priority: e.target.value })}
                    sx={{ height: 32, fontSize: '0.8rem' }}
                  >
                    <MenuItem value="urgent">Urgent</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              )}
            </Box>

            {/* Description + Replies */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {/* Original description */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                    {(selectedTicket.userName || selectedTicket.requesterName || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedTicket.userName || selectedTicket.requesterName || selectedTicket.requesterEmail || 'User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(parseISO(selectedTicket.createdAt), { addSuffix: true })}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {selectedTicket.description}
                </Typography>
              </Paper>

              {/* Replies */}
              {selectedTicket.replies?.map((reply) => (
                <Paper
                  key={reply.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: reply.isInternalNote ? 'warning.main' : 'divider',
                    bgcolor: reply.isInternalNote
                      ? 'rgba(255, 152, 0, 0.04)'
                      : reply.isStaffReply
                        ? 'rgba(33, 150, 243, 0.04)'
                        : 'background.default',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: '0.75rem',
                        bgcolor: reply.isStaffReply ? 'info.main' : 'grey.500',
                      }}
                    >
                      {(reply.userName || reply.senderName || reply.userEmail || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {reply.userName || reply.senderName || reply.userEmail || 'Staff'}
                    </Typography>
                    {reply.isStaffReply && (
                      <Chip size="small" label="Staff" color="info" sx={{ height: 18, fontSize: '0.6rem' }} />
                    )}
                    {reply.isInternalNote && (
                      <Chip size="small" icon={<InternalIcon sx={{ fontSize: 12 }} />} label="Internal Note" color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(parseISO(reply.createdAt), { addSuffix: true })}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {reply.message}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {/* Reply box */}
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
                placeholder={isInternalNote ? 'Add an internal note...' : 'Type your reply...'}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                size="small"
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderColor: isInternalNote ? 'warning.main' : undefined,
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {isAdmin ? (
                  <Button
                    size="small"
                    variant={isInternalNote ? 'contained' : 'text'}
                    color="warning"
                    startIcon={<InternalIcon />}
                    onClick={() => setIsInternalNote(!isInternalNote)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Internal Note
                  </Button>
                ) : (
                  <Box />
                )}
                <Button
                  variant="contained"
                  size="small"
                  endIcon={replying ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                  onClick={handleReply}
                  disabled={!replyText.trim() || replying}
                  sx={{ textTransform: 'none' }}
                >
                  {isInternalNote ? 'Add Note' : 'Send Reply'}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Top bar */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'background.paper',
        }}
      >
        <TicketIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          {isAdmin ? 'Support Tickets' : 'My Tickets'}
        </Typography>
        <Chip
          size="small"
          label={`${total} tickets`}
          sx={{ ml: 0.5, height: 22, fontSize: '0.7rem' }}
        />
        <Box sx={{ flex: 1 }} />
        {isAdmin && (
        <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', height: 32 }}>
          <Tooltip title="List View">
            <IconButton
              size="small"
              onClick={() => setView('list')}
              sx={{
                borderRadius: 0,
                px: 1.5,
                bgcolor: view === 'list' ? 'action.selected' : 'transparent',
                color: view === 'list' ? 'primary.main' : 'text.secondary',
              }}
            >
              <ViewListIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Dashboard">
            <IconButton
              size="small"
              onClick={() => setView('dashboard')}
              sx={{
                borderRadius: 0,
                px: 1.5,
                bgcolor: view === 'dashboard' ? 'action.selected' : 'transparent',
                color: view === 'dashboard' ? 'primary.main' : 'text.secondary',
              }}
            >
              <DashboardIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => { fetchTickets(); fetchStats(); }}>
            <RefreshIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          New Ticket
        </Button>
      </Box>

      {/* Main content */}
      {view === 'dashboard' ? (
        <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
          {renderStatsCards()}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Recent Tickets
          </Typography>
          {renderTicketList()}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {renderTicketList()}
          {selectedTicket ? (
            renderTicketDetail()
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: 'text.secondary',
              }}
            >
              <TicketIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Select a ticket to view details
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Or create a new ticket with the button above
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Create ticket dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create New Ticket</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Subject"
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newCategory}
                label="Category"
                onChange={(e) => setNewCategory(e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={newPriority}
                label="Priority"
                onChange={(e) => setNewPriority(e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newSubject.trim() || !newDescription.trim() || creating}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ textTransform: 'none' }}
          >
            Create Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const SupportPageWithBoundary: React.FC = () => (
  <SupportErrorBoundary>
    <SupportPage />
  </SupportErrorBoundary>
);

export default SupportPageWithBoundary;
